import React from "react";

// Returns a camera layout config based on the phone model name
export function getCameraLayout(model) {
  const m = (model || "").toLowerCase();

  // ============== iPhone ==============
  if (m.includes("iphone")) {
    if (m.includes("pro max") || m.includes("pro")) return { type: "iphone-pro" };
    if (m.match(/iphone\s*(11|12|13|14|15|16)/)) return { type: "iphone-2cam" };
    if (m.includes("iphone xs") || m.includes("iphone xr") || m.match(/iphone x(\s|$)/)) return { type: "iphone-2cam-vertical" };
    if (m.includes("iphone se") || m.includes("iphone 8") || m.includes("iphone 7")) return { type: "iphone-single" };
    return { type: "iphone-pro" };
  }
  // ============== Samsung ==============
  if (m.includes("samsung")) {
    if (m.includes("ultra")) return { type: "samsung-ultra" };
    if (m.includes("note")) return { type: "samsung-note" };
    if (m.includes("z fold")) return { type: "samsung-fold" };
    if (m.includes("z flip")) return { type: "samsung-flip" };
    if (m.includes("galaxy a")) return { type: "samsung-a" };
    return { type: "samsung-standard" };
  }
  // ============== Xiaomi ==============
  if (m.includes("xiaomi")) {
    if (m.includes("ultra") || (m.includes("xiaomi") && m.includes("pro") && !m.includes("poco"))) return { type: "xiaomi-leica" };
    if (m.includes("poco")) return { type: "poco" };
    return { type: "xiaomi-standard" };
  }
  // ============== Redmi ==============
  if (m.includes("redmi")) {
    if (m.includes("note")) return { type: "redmi-note" };
    return { type: "redmi-basic" };
  }
  // ============== Huawei ==============
  if (m.includes("huawei")) {
    if (m.match(/(p60|p50|p40|mate 60|mate 50|mate 40)/)) return { type: "huawei-ring" };
    return { type: "huawei-square" };
  }
  // ============== Honor ==============
  if (m.includes("honor")) {
    if (m.includes("magic")) return { type: "honor-ring" };
    return { type: "honor-square" };
  }
  // ============== Motorola ==============
  if (m.includes("motorola")) {
    if (m.includes("edge")) return { type: "motorola-edge" };
    if (m.includes("razr")) return { type: "motorola-razr" };
    return { type: "motorola-g" };
  }
  return { type: "iphone-pro" };
}


// ============== Visual primitives ==============
const Lens = ({ className = "" }) => (
  <div className={`rounded-full bg-gradient-to-br from-slate-700 to-slate-950 ring-2 ring-slate-700/60 flex items-center justify-center shadow-inner ${className}`}>
    <div className="w-[55%] h-[55%] rounded-full bg-gradient-to-br from-slate-900 to-black ring-1 ring-cyan-500/20 flex items-center justify-center">
      <div className="w-[35%] h-[35%] rounded-full bg-cyan-400/20" />
    </div>
  </div>
);

const Flash = ({ className = "" }) => (
  <div className={`rounded-full bg-amber-100/80 ring-2 ring-slate-700 flex items-center justify-center ${className}`}>
    <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
  </div>
);

const SmallSensor = ({ className = "" }) => (
  <div className={`rounded-full bg-slate-700 ring-1 ring-white/10 ${className}`} />
);


// ============== Layouts ==============
export default function CameraModule({ layout }) {
  const t = layout?.type || "iphone-pro";

  if (t === "iphone-pro") {
    return (
      <>
        <div className="absolute top-5 left-5 w-[38%] aspect-square rounded-[26px] bg-gradient-to-br from-slate-800 to-black p-2.5 shadow-lg ring-1 ring-white/10">
          <div className="grid grid-cols-2 grid-rows-2 gap-1.5 h-full">
            <Lens /><Lens /><Lens /><Flash />
          </div>
        </div>
        <SmallSensor className="absolute top-[14%] left-[42%] w-3 h-3" />
      </>
    );
  }

  if (t === "iphone-2cam") {
    return (
      <div className="absolute top-5 left-5 w-[34%] aspect-square rounded-[24px] bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-1 ring-white/10">
        <div className="relative h-full">
          <Lens className="absolute top-0 left-0 w-[58%] aspect-square" />
          <Lens className="absolute bottom-0 right-0 w-[58%] aspect-square" />
          <Flash className="absolute top-1.5 right-1.5 w-3 h-3" />
        </div>
      </div>
    );
  }

  if (t === "iphone-2cam-vertical") {
    return (
      <div className="absolute top-5 left-5 w-[20%] aspect-[1/2] rounded-2xl bg-gradient-to-br from-slate-800 to-black p-1.5 shadow-lg ring-1 ring-white/10 flex flex-col gap-1.5">
        <Lens className="flex-1" />
        <Lens className="flex-1" />
        <Flash className="w-2.5 h-2.5 self-center" />
      </div>
    );
  }

  if (t === "iphone-single") {
    return (
      <div className="absolute top-5 left-5 w-[18%] aspect-square">
        <Lens />
      </div>
    );
  }

  if (t === "samsung-ultra") {
    return (
      <div className="absolute top-6 left-6 flex flex-col gap-2 items-start">
        <Lens className="w-12 h-12" />
        <Lens className="w-9 h-9" />
        <Lens className="w-9 h-9" />
        <div className="flex items-center gap-1.5">
          <Lens className="w-6 h-6" />
          <Flash className="w-4 h-4" />
        </div>
      </div>
    );
  }

  if (t === "samsung-standard" || t === "samsung-fold") {
    return (
      <div className="absolute top-6 left-6 flex flex-col gap-2 items-start">
        <Lens className="w-10 h-10" />
        <Lens className="w-10 h-10" />
        <Lens className="w-10 h-10" />
        <Flash className="w-4 h-4 ml-1" />
      </div>
    );
  }

  if (t === "samsung-a") {
    return (
      <div className="absolute top-5 left-5 w-[34%] aspect-square rounded-[20px] bg-gradient-to-br from-slate-800 to-black p-2.5 shadow-lg ring-1 ring-white/10">
        <div className="grid grid-cols-2 grid-rows-2 gap-1.5 h-full">
          <Lens /><Lens /><Lens /><Flash />
        </div>
      </div>
    );
  }

  if (t === "samsung-note") {
    return (
      <div className="absolute top-5 left-1/2 -translate-x-1/2 w-[60%] h-[14%] rounded-2xl bg-gradient-to-br from-slate-800 to-black p-2 shadow-lg ring-1 ring-white/10 flex items-center justify-around">
        <Lens className="w-9 h-9" />
        <Lens className="w-9 h-9" />
        <Lens className="w-9 h-9" />
        <Flash className="w-3 h-3" />
      </div>
    );
  }

  if (t === "samsung-flip") {
    return (
      <div className="absolute top-5 left-5 flex gap-2">
        <Lens className="w-9 h-9" />
        <Lens className="w-9 h-9" />
      </div>
    );
  }

  if (t === "xiaomi-leica") {
    // Large square island, top-center, with circular pattern of lenses (Leica style)
    return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[58%] aspect-square rounded-[28px] bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-1 ring-white/10">
        <div className="relative w-full h-full rounded-full bg-slate-900/60 ring-2 ring-slate-700/40">
          <Lens className="absolute top-1 left-1/2 -translate-x-1/2 w-[36%] aspect-square" />
          <Lens className="absolute bottom-2 left-2 w-[36%] aspect-square" />
          <Lens className="absolute bottom-2 right-2 w-[36%] aspect-square" />
          <Flash className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3" />
        </div>
        <div className="absolute -top-1 right-2 text-[8px] uppercase tracking-widest font-black text-amber-400 bg-black/60 px-1.5 py-0.5 rounded">Leica</div>
      </div>
    );
  }

  if (t === "xiaomi-standard") {
    return (
      <div className="absolute top-5 left-5 w-[34%] aspect-square rounded-[22px] bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-1 ring-white/10">
        <div className="flex flex-col gap-2 h-full justify-around">
          <div className="flex justify-around items-center">
            <Lens className="w-[40%] aspect-square" />
            <Lens className="w-[40%] aspect-square" />
          </div>
          <div className="flex justify-around items-center">
            <Lens className="w-[40%] aspect-square" />
            <Flash className="w-3 h-3" />
          </div>
        </div>
      </div>
    );
  }

  if (t === "poco") {
    return (
      <div className="absolute top-5 left-5 w-[42%] h-[18%] rounded-2xl bg-gradient-to-br from-slate-800 to-black p-2 shadow-lg ring-1 ring-white/10 flex items-center gap-2 px-3">
        <Lens className="w-9 h-9" />
        <Lens className="w-7 h-7" />
        <div className="ml-auto flex flex-col gap-1 items-end">
          <Flash className="w-2 h-2" />
          <SmallSensor className="w-2 h-2" />
        </div>
      </div>
    );
  }

  if (t === "redmi-note") {
    return (
      <div className="absolute top-5 left-5 w-[36%] aspect-square rounded-[22px] bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-1 ring-white/10">
        <div className="grid grid-cols-2 gap-2 h-full">
          <Lens className="row-span-2" />
          <Lens />
          <div className="flex items-center justify-center gap-1">
            <Lens className="w-[60%] aspect-square" />
            <Flash className="w-2.5 h-2.5" />
          </div>
        </div>
      </div>
    );
  }

  if (t === "redmi-basic") {
    return (
      <div className="absolute top-5 left-5 w-[28%] aspect-[1/1.4] rounded-2xl bg-gradient-to-br from-slate-800 to-black p-2 shadow-lg ring-1 ring-white/10 flex flex-col gap-2 items-center justify-center">
        <Lens className="w-9 h-9" />
        <Lens className="w-7 h-7" />
        <Flash className="w-2.5 h-2.5" />
      </div>
    );
  }

  if (t === "huawei-ring") {
    return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[55%] aspect-square rounded-full bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-2 ring-slate-700/50">
        <div className="relative w-full h-full rounded-full ring-2 ring-slate-700/60">
          <Lens className="absolute top-1 left-1/2 -translate-x-1/2 w-[34%] aspect-square" />
          <Lens className="absolute bottom-2 left-2 w-[34%] aspect-square" />
          <Lens className="absolute bottom-2 right-2 w-[34%] aspect-square" />
          <Flash className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3" />
        </div>
      </div>
    );
  }

  if (t === "huawei-square") {
    return (
      <div className="absolute top-5 left-5 w-[34%] aspect-square rounded-[24px] bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-1 ring-white/10">
        <div className="grid grid-cols-2 grid-rows-2 gap-1.5 h-full">
          <Lens /><Lens /><Lens /><Flash />
        </div>
      </div>
    );
  }

  if (t === "honor-ring") {
    return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[52%] aspect-square rounded-full bg-gradient-to-br from-slate-800 to-black p-3 shadow-lg ring-2 ring-slate-700/40">
        <div className="relative w-full h-full rounded-full ring-2 ring-slate-700/60 bg-slate-900/40">
          <Lens className="absolute top-2 left-2 w-[38%] aspect-square" />
          <Lens className="absolute top-2 right-2 w-[38%] aspect-square" />
          <Lens className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[38%] aspect-square" />
          <Flash className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5" />
        </div>
      </div>
    );
  }

  if (t === "honor-square") {
    return (
      <div className="absolute top-5 left-5 w-[32%] aspect-square rounded-[20px] bg-gradient-to-br from-slate-800 to-black p-2.5 shadow-lg ring-1 ring-white/10">
        <div className="flex flex-col gap-1.5 h-full">
          <Lens className="flex-1 self-start aspect-square" />
          <div className="flex gap-1.5 flex-1">
            <Lens className="aspect-square" />
            <Flash className="w-3 h-3 self-end" />
          </div>
        </div>
      </div>
    );
  }

  if (t === "motorola-edge") {
    return (
      <div className="absolute top-5 left-5 w-[16%] aspect-[1/3] rounded-full bg-gradient-to-br from-slate-800 to-black p-1.5 shadow-lg ring-1 ring-white/10 flex flex-col gap-1.5 items-center">
        <Lens className="w-full aspect-square" />
        <Lens className="w-full aspect-square" />
        <Lens className="w-[80%] aspect-square" />
        <Flash className="w-2.5 h-2.5" />
      </div>
    );
  }

  if (t === "motorola-razr") {
    return (
      <div className="absolute top-5 left-5 flex gap-2">
        <Lens className="w-10 h-10" />
        <Lens className="w-10 h-10" />
      </div>
    );
  }

  if (t === "motorola-g") {
    return (
      <div className="absolute top-5 left-5 w-[28%] aspect-square rounded-2xl bg-gradient-to-br from-slate-800 to-black p-2 shadow-lg ring-1 ring-white/10">
        <div className="grid grid-cols-2 gap-1.5 h-full">
          <Lens /><Lens /><Lens /><Flash />
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="absolute top-5 left-5 w-[34%] aspect-square rounded-[22px] bg-gradient-to-br from-slate-800 to-black p-2.5 shadow-lg ring-1 ring-white/10">
      <div className="grid grid-cols-2 grid-rows-2 gap-1.5 h-full">
        <Lens /><Lens /><Lens /><Flash />
      </div>
    </div>
  );
}
